// PaymentMethod.tsx
import React, { useState } from 'react';
import {
  CardNumberElement,
  CardExpiryElement,
  CardCvcElement,
  useStripe,
} from '@stripe/react-stripe-js';
import {useElements } from '@stripe/react-stripe-js';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  Box,
  FormControlLabel,
  Checkbox,
  DialogActions,
  Button,
} from '@mui/material';
import { toast } from 'react-toastify';
import { useDispatch } from 'react-redux';
import { createSetupIntent } from '@/store/Payment';

const ELEMENT_OPTIONS = {
  style: {
    base: {
      fontSize: '16px',
      color: '#374151',
      '::placeholder': { color: '#9ca3af' },
    },
    invalid: { color: '#dc2626' },
  },
};

interface CampaignInsightsDialogProps {
  open: boolean;
  onClose: () => void;
}

export const AddPaymentCard: React.FC<CampaignInsightsDialogProps> = ({
  open,
  onClose,
}) => {
  const [cardHolderName, setCardHolderName] = useState('');
  const [ack, setAck] = React.useState(false);

  const elements = useElements();
  const stripe = useStripe();
  const dispatch = useDispatch<any>()

  const handleClose = () => {
    setAck(false);
    onClose();
  };

  const getCardElement = () => {
    const card = elements?.getElement('cardNumber');
    if (!card) toast.error('Card Number is Missing');
    return card;
  };

  const handleConfirm = async () => {
    const setupRes = await dispatch(createSetupIntent());
    const clientSecret = setupRes.payload.clientSecret;

    const card = getCardElement();
    if (!card || !stripe) return;

    const result = await stripe.confirmCardSetup(clientSecret, {
      payment_method: {
        card,
        billing_details: { name: cardHolderName },
      },
    });

    if (result.error) {
      toast.error(`❌ Setup failed: ${result.error.message}`);
      return null;
    }

    toast.success('Card saved successfully');

    setAck(false);
    onClose();
  };

  return (
      <Dialog
        open={open}
        onClose={onClose}
        // disable the theme’s maxWidth breakpoint
        maxWidth={false}
        // remove fullWidth so it won’t stretch to 100%
        fullWidth={false}
        PaperProps={{
          sx: {
            width: '506px',
            background: '#FAFAFA',
            borderRadius: '24px',
          },
        }}
      >
        <DialogTitle
          sx={{
            background: '#FFFFFF',
            textAlign: 'center',
            pt: '13px',
            pb: '13px',
            fontSize: '32px',
          }}
        >
          Add New Payment Method
        </DialogTitle>

        <DialogContent
          sx={{
            background: '#FAFAFA',
          }}
        >
          <div className='pt-5 space-y-5'>
            {/* Cardholder Name */}
            <div>
              <label className='block text-sm font-medium text-gray-700'>
                Holder Name
              </label>
              <input
                type='text'
                value={cardHolderName}
                onChange={(e) => setCardHolderName(e.target.value)}
                placeholder='Enter holder name here'
                className='mt-1 block w-full border border-gray-300 rounded-lg px-3 py-2 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-pink-500 focus:border-pink-500'
              />
            </div>

            {/* Card Number */}
            <div>
              <label className='block text-sm font-medium text-gray-700'>
                Card Number
              </label>
              <div className='mt-1 block w-full border border-gray-300 rounded-lg px-3 py-2 focus-within:ring-2 focus-within:ring-pink-500 focus-within:border-pink-500'>
                <CardNumberElement options={ELEMENT_OPTIONS} />
              </div>
            </div>

            {/* Expiry & CVV & Amount */}
            <div className='grid grid-cols-3 gap-4'>
              <div>
                <label className='block text-sm font-medium text-gray-700'>
                  Expiry
                </label>
                <div className='mt-1 block w-full border border-gray-300 rounded-lg px-3 py-2 focus-within:ring-2 focus-within:ring-pink-500 focus-within:border-pink-500'>
                  <CardExpiryElement options={ELEMENT_OPTIONS} />
                </div>
              </div>
              <div>
                <label className='block text-sm font-medium text-gray-700'>
                  CVC
                </label>
                <div className='mt-1 block w-full border border-gray-300 rounded-lg px-3 py-2 focus-within:ring-2 focus-within:ring-pink-500 focus-within:border-pink-500'>
                  <CardCvcElement options={ELEMENT_OPTIONS} />
                </div>
              </div>
            </div>
            <Box mt={2} display='flex' justifyContent='center'>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={ack}
                    onChange={(e) => setAck(e.target.checked)}
                  />
                }
                label='I Acknowledge'
                sx={{
                  m: 0,
                  '& .MuiFormControlLabel-label': {
                    fontSize: 16,
                    color: 'black',
                  },
                }}
              />
            </Box>
          </div>
        </DialogContent>
        <DialogActions sx={{ justifyContent: 'center', gap: 2, pb: 2 }}>
          <Button
            variant='outlined'
            disabled={!ack}
            onClick={handleConfirm}
            sx={{
              textTransform: 'none',
              borderRadius: 100,
              px: 4,
              color: 'black',
              borderColor: 'black',
            }}
          >
            Yes, Confirm
          </Button>
          <Button
            variant='contained'
            onClick={handleClose}
            sx={{
              textTransform: 'none',
              borderRadius: 100,
              px: 4,
              bgcolor: '#FF0050',
              '&:hover': { bgcolor: '#E6004F' },
            }}
          >
            No, Cancel
          </Button>
        </DialogActions>
      </Dialog>
  );
};
